import { useMemo, useCallback } from 'react'
import { hasPermission, useAuth } from '@cooltrax/jellyfish-sdk'

export const usePermissions = () => {
  const { token, isLoading } = useAuth()

  // Force refresh permissions
  const refreshPermissions = useCallback(() => {
    // This is now handled by the auth context
    // Just trigger a re-render by creating a new object reference
  }, [])

  const permissions = useMemo(() => {
    return {
      hasUiDev: hasPermission(token, 'ui:dev'),
      hasPermission: (permission: string) => hasPermission(token, permission),
      getAvailableRoutes: () => {
        const hasUiDev = hasPermission(token, 'ui:dev')
        const hasAuditAccess = hasPermission(token, 'audit:r*') || hasPermission(token, 'audit:r')

        if (hasUiDev) {
          const path = [
            '/fresh/*',
            '/fresh/in-transit',
            '/fresh/in-store',
            '/fresh/geofences',
            '/fresh/reports',
            '/fresh/alerts',
            '/fresh/admin/users',
            '/fresh/admin/roles',
            '/fresh/admin/ccp',
            '/fresh/admin/upload',
            '/fresh/admin/settings',
            '/fresh/admin/audit',
          ]
          if (hasPermission(token, 'insights:r')) {
            path.push('/insights/dashboards')
          }

          return path
        }

        if (hasAuditAccess) {
          return ['/fresh/admin/audit']
        }

        return []
      },
      getFirstAvailableRoute: () => {
        if (isLoading) return '/'
        const hasUiDev = hasPermission(token, 'ui:dev')
        const hasAuditAccess = hasPermission(token, 'audit:r*') || hasPermission(token, 'audit:r')

        if (hasUiDev && hasPermission(token, 'insights:r')) {
          return '/insights/dashboards'
        }

        if (hasUiDev) {
          return '/fresh/in-transit'
        }

        if (hasAuditAccess) {
          return '/fresh/admin/audit'
        }

        // All authenticated users can access settings
        return '/fresh/settings'
      },
      refreshPermissions,
      isLoading, // Expose loading state from auth context
      // Reports permissions
      hasReportsRead: hasPermission(token, 'reports:read'),
      hasReportsWrite: hasPermission(token, 'reports:write'),
      hasReportsSchedule: hasPermission(token, 'reports:schedule'),
    }
  }, [token, isLoading])

  return permissions
}
